import { useCallback, useEffect, useState } from 'react'

import { useRouter } from 'next/router'

import playcanvasConfig from '~/config/playcanvas'

import useCurrentUser from '~/hooks/useCurrentUser'

import playCanvasAnalytics from '~/analytics/playcanvas/playcanvasAnalyticsEventLogger'

import { useGetAvatarMetadataQuery } from '~/redux/apis/user-api'

import { useGetPetStateQuery } from '~/shared/apis/pet-state.api'

import { TREASURETRAIL_REQUEST } from '~/games/constants/games.const'
import useGameRunTimer from '~/games/hooks/useGameRunTimer'

import ROUTES from '~/shared/constants/routes.const'

import { useGetUserItemsQuery } from '~/redux/apis/gem-shop-api'

import { useSetRewardsMutation } from '~/redux/apis/reward-api'
import { REWARD_EVENT, REWARD_FEATURE } from '~/gamification/types/gamification.types'

import { useSetGameStatsMutation, useLazyGetSummaryQuery } from '../apis/treasureTrail-api'

function useTreasureTrail(showBanner: (showTopbar: boolean) => void) {
  const [treasureTrailIframe, setMultIframe] = useState<HTMLIFrameElement | undefined>(undefined)

  const [setRewards] = useSetRewardsMutation()
  const [isReady, setIsReady] = useState(false)
  const user = useCurrentUser()
  const { data: avatarMetadata } = useGetAvatarMetadataQuery(user, { skip: !user?.avatarUrl })
  const { data: petState } = useGetPetStateQuery(user?.id as number, { skip: !user?.id })

  const router = useRouter()

  const language = router.locale

  const { menuPage } = router.query

  const isMenuPage = menuPage === 'true'

  const [getSummary] = useLazyGetSummaryQuery()
  const [setGameStats] = useSetGameStatsMutation()

  // The game reports a finished puzzle but never when it was started, so the window is inferred.
  const takeRunWindow = useGameRunTimer(isReady)

  const userItems = useGetUserItemsQuery(undefined, { skip: !user?.id })

  const sendMessageToIframe = useCallback(
    (data: unknown) => {
      const postMessage = treasureTrailIframe?.contentWindow

      if (postMessage) {
        postMessage.postMessage(data, playcanvasConfig.treasureTrailURL.origin)
      }
    },
    [treasureTrailIframe?.contentWindow],
  )

  useEffect(
    function initialize() {
      const postMessage = treasureTrailIframe?.contentWindow
      if (isReady && postMessage) {
        if (user && language && petState) {
          const initialData = {
            type: TREASURETRAIL_REQUEST.INITIAL_DATA,
            user: {
              avatar: avatarMetadata ? avatarMetadata : null,
            },
            petState,
            language,
            userItems: userItems.data,
            menuPage: Boolean(isMenuPage),
          }
          sendMessageToIframe(initialData)
        }
      }
    },
    [
      isReady,
      user,
      avatarMetadata,
      language,
      petState,
      sendMessageToIframe,
      userItems.data,
      treasureTrailIframe?.contentWindow,
      isMenuPage,
    ],
  )

  const messageHandler = useCallback(
    async (event: any): Promise<void> => {
      if (event.origin === playcanvasConfig.treasureTrailURL.origin) {
        try {
          const data = event.data
          if (data.type === TREASURETRAIL_REQUEST.INITIAL_DATA) {
            setIsReady(true)
          }
          if (data.type === TREASURETRAIL_REQUEST.HIDE_BANNER) {
            showBanner(false)
          }
          if (data.type === TREASURETRAIL_REQUEST.SHOW_BANNER) {
            showBanner(true)
          }
          if (data.type === TREASURETRAIL_REQUEST.SHOW_BANNER) {
            showBanner(true)
          }
          if (data.type === TREASURETRAIL_REQUEST.MAKE_AVATAR) {
            router.replace(`${ROUTES.AVATAR}?from=treasure-trail`)
          }

          if (data.type === TREASURETRAIL_REQUEST.GET_USERDATA) {
            const res = await getSummary().unwrap()
            const msg = {
              type: TREASURETRAIL_REQUEST.GET_USERDATA,
              userStats: res,
            }
            sendMessageToIframe(msg)
          }

          if (data.type === TREASURETRAIL_REQUEST.PLAYCANVAS_ANALYTICS_EVENT) {
            playCanvasAnalytics.logPlayCanvasEvent(data)
            if (data?.data && data?.data?.event === REWARD_EVENT.REWARDS) {
              const reward = {
                xp: data?.data?.experience_points || 0,
                kudos: data?.data?.kudos || 0,
                gems: data?.data?.gems || 0,
                feature: REWARD_FEATURE.GAME_TREASURE_TRAIL,
                event_id: `${REWARD_FEATURE.GAME_TREASURE_TRAIL}#${REWARD_EVENT.GAME_ENDED}`,
              }

              let type = 'no reward'
              let res
              if (reward.gems || reward.kudos || reward.xp) {
                res = await setRewards(reward)
                type = data?.data?.event || REWARD_EVENT.REWARDS
              }
              const msg = {
                type,
                response: res,
              }
              sendMessageToIframe(msg)
            }
          }

          if (data.type === TREASURETRAIL_REQUEST.SET_USERDATA) {
            const res = await setGameStats({ ...data.userStats.msg, path: ROUTES.TREASURETRAIL, ...takeRunWindow() })
            const msg = {
              type: TREASURETRAIL_REQUEST.SET_USERDATA,
              userStats: res,
            }
            sendMessageToIframe(msg)
          }
        } catch (error) {
          sendMessageToIframe('Something Wrong')
        }
      }
    },
    [getSummary, sendMessageToIframe, setRewards, setGameStats, showBanner, router, takeRunWindow],
  )
  useEffect(() => {
    setMultIframe(document.getElementById('treasureTrailIframe') as HTMLIFrameElement)
  }, [])

  useEffect(() => {
    window.addEventListener('message', messageHandler)
    return () => window.removeEventListener('message', messageHandler)
  }, [messageHandler])
  return {
    treasureTrailURL: playcanvasConfig.treasureTrailURL.href,
  }
}

export default useTreasureTrail
